// src/pages/team/PublicTeamPage.tsx — a team's roster, readable by anyone.
//
// Separate from TeamPage, which is the owner's console (add, drop, swap,
// lineup). Threading read-only through several hundred lines of mutation UI
// would be riskier and worse to read than a page that only shows.
//
// Presentation deliberately reuses the Players register vocabulary — same row
// shape, same headshot, same position chip, same Anton average, same scouting
// card on click. A player should look like the same player wherever you meet
// them; a second roster style would be drift, not design.
//
// Served at /teams/:teamId outside auth, so nothing here assumes an account.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { League, Player, RosterSlot, Team } from '../../types';
import BackLink from '../../components/common/BackLink';
import PlayerScoutingCard, { Headshot, positionChip } from '../../components/players/PlayerScoutingCard';
import { seasonAverage } from '../../utils/playerSeason';

/** Ledger figure: Anton, tabular, ink. The record-book device used throughout. */
const Figure: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
  <div>
    <div className="font-display text-2xl leading-none text-ink-900 tabular-nums">{value}</div>
    <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-secondary-500 mt-1">
      {label}
    </div>
  </div>
);

/** Skeleton rows rather than a spinner: the shape of what is coming, not a
 *  hole where content will be. */
const RosterSkeleton: React.FC = () => (
  <ul className="divide-y divide-secondary-200" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <li key={i} className="flex items-center gap-4 py-3">
        <div className="w-12 h-12 bg-secondary-100 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="h-3.5 w-40 bg-secondary-100 animate-pulse" />
          <div className="h-3 w-24 bg-secondary-100 animate-pulse mt-2" />
        </div>
        <div className="h-7 w-12 bg-secondary-100 animate-pulse" />
      </li>
    ))}
  </ul>
);

export default function PublicTeamPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuth();

  const [team, setTeam] = useState<Team | null>(null);
  const [league, setLeague] = useState<League | null>(null);
  const [roster, setRoster] = useState<RosterSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState<Player | null>(null);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const t = await apiService.getTeam(Number(teamId));
      setTeam(t);
      const [lg, slots] = await Promise.all([
        apiService.getLeague(t.league_id).catch(() => null),
        apiService.getRoster(Number(teamId)).catch(() => [] as RosterSlot[]),
      ]);
      setLeague(lg);
      setRoster(slots ?? []);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="py-20 text-center">
        <p className="font-display uppercase text-2xl text-ink-900">No such team</p>
        <p className="text-sm text-secondary-600 mt-2">
          That team does not exist, or the link is wrong.
        </p>
        <Link
          to={user ? '/app/leagues' : '/browse'}
          className="btn-primary inline-block mt-6 uppercase tracking-wider"
        >
          {user ? 'Back to my leagues' : 'Browse open leagues'}
        </Link>
      </div>
    );
  }

  const starters = roster.filter((s) => s.is_starter);
  const bench = roster.filter((s) => !s.is_starter);
  const record = `${team?.wins ?? 0}–${team?.losses ?? 0}`;

  const rosterRow = (slot: RosterSlot) => {
    const player = slot.player;
    if (!player) return null;
    const team_ = (player as any).nfl_team || (player as any).nflTeam;
    const jersey = (player as any).jersey_number ?? (player as any).jerseyNumber;
    const season = seasonAverage(player);
    const experience = (player as any).experience ?? 0;

    return (
      <li key={slot.id}>
        <button
          type="button"
          onClick={() => setSelected(player)}
          data-testid="roster-player"
          aria-label={`Scouting report for ${player.name}`}
          className="group w-full flex items-center gap-4 py-3 px-2 -mx-2 text-left transition-colors
                     hover:bg-secondary-50 focus:outline-none focus-visible:bg-secondary-50
                     focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-0"
        >
          <Headshot player={player} team={team_} jersey={jersey} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-semibold text-ink-900 truncate group-hover:underline">
                {player.name}
              </span>
              <span className={`px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase ${positionChip(player.position)}`}>
                {player.position}
              </span>
              {jersey ? <span className="text-xs text-secondary-500">#{jersey}</span> : null}
            </div>
            <div className="mt-0.5 text-sm text-secondary-500">
              {team_?.abbreviation || team_?.city || 'Free agent'} · {experience} yr
              {experience === 1 ? '' : 's'} exp
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-2xl leading-none text-ink-900 tabular-nums">
              {season.display}
            </div>
            <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-secondary-500">
              {season.label}
            </div>
          </div>
        </button>
      </li>
    );
  };

  const section = (title: string, slots: RosterSlot[], emptyCopy: string) => (
    <section className="mb-10">
      <div className="flex items-baseline gap-2 border-b-2 border-ink-900 pb-1.5 mb-1">
        <h2 className="font-display uppercase text-lg tracking-wide text-ink-900">{title}</h2>
        <span className="font-display text-sm text-secondary-400 tabular-nums">
          {slots.length}
        </span>
      </div>
      {loading ? (
        <RosterSkeleton />
      ) : slots.length === 0 ? (
        <p className="py-6 text-sm text-secondary-500">{emptyCopy}</p>
      ) : (
        <ul className="divide-y divide-secondary-200">{slots.map(rosterRow)}</ul>
      )}
    </section>
  );

  return (
    <div>
      {user && league ? (
        <BackLink to={`/app/leagues/${league.id}`}>Back to {league.name}</BackLink>
      ) : (
        <BackLink to="/browse">Back to open leagues</BackLink>
      )}

      {/* Masthead — the team as a record-book entry */}
      <header className="border-b-2 border-ink-900 pb-5 mt-4 mb-8">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-gold-700 mb-1.5">
          {league ? league.name : 'Team sheet'}
        </p>
        <h1 className="font-display uppercase text-ink-900 text-4xl sm:text-5xl leading-[0.95] text-balance">
          {loading && !team ? (
            <span className="inline-block h-9 w-64 max-w-full bg-secondary-100 animate-pulse align-middle" />
          ) : (
            team?.team_name
          )}
        </h1>
        {!loading && team && (
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4 mt-5">
            <Figure value={record} label="Record" />
            <Figure value={Number(team.points_for ?? 0).toFixed(1)} label="Points for" />
            <Figure value={Number(team.points_against ?? 0).toFixed(1)} label="Against" />
            <Figure value={roster.length} label="On roster" />
          </div>
        )}
      </header>

      {section('Starters', starters, 'No starters set for this week.')}
      {section('Bench', bench, 'Nobody on the bench.')}

      {!loading && !user && (
        <aside className="border-2 border-ink-900 bg-secondary-50 p-6">
          <p className="font-display uppercase text-ink-900 text-xl leading-none">
            Kickers and punters only
          </p>
          <p className="text-sm text-secondary-700 mt-2 max-w-prose">
            That is the whole league. No quarterbacks, no running backs, no excuses —
            just the people who actually have to make the kick.
          </p>
          <Link to="/browse" className="btn-primary inline-block mt-4 uppercase tracking-wider">
            Find a league
          </Link>
        </aside>
      )}

      {selected && (
        <PlayerScoutingCard player={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
